/**
 * DashboardSelector — renders a tab bar or dropdown for switching between
 * registered dashboard plugins.
 *
 * Uses MUI Tabs when there are 2-5 dashboards, or MUI Select when there
 * are more. If only 1 dashboard is registered, nothing is rendered.
 */

import React from "react";
import {
  Box,
  Tab,
  Tabs,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { DashboardPlugin } from "./types";

interface DashboardSelectorProps {
  dashboards: DashboardPlugin[];
  selectedId: string;
  onSelect: (id: string) => void;
}

const DashboardSelector: React.FC<DashboardSelectorProps> = ({
  dashboards,
  selectedId,
  onSelect,
}) => {
  const theme = useTheme();

  // Don't render if there's only one (or zero) dashboards
  if (dashboards.length <= 1) {
    return null;
  }

  // Use Tabs for 2-5 dashboards
  if (dashboards.length <= 5) {
    return (
      <Box sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={selectedId}
          onChange={(_e, newValue: string) => onSelect(newValue)}
          aria-label="Dashboard selector"
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              minHeight: 48,
              fontWeight: theme.typography.fontWeightMedium,
            },
          }}
        >
          {dashboards.map((d) => (
            <Tab
              key={d.id}
              value={d.id}
              label={d.displayName}
              icon={<>{d.icon}</>}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Box>
    );
  }

  // Use Select for more than 5 dashboards
  return (
    <Box sx={{ mb: 2, maxWidth: 320 }}>
      <FormControl fullWidth size="small">
        <InputLabel id="dashboard-selector-label">Dashboard</InputLabel>
        <Select
          labelId="dashboard-selector-label"
          value={selectedId}
          label="Dashboard"
          onChange={(e) => onSelect(e.target.value as string)}
        >
          {dashboards.map((d) => (
            <MenuItem key={d.id} value={d.id}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ListItemIcon sx={{ minWidth: 0 }}>{d.icon}</ListItemIcon>
                <ListItemText primary={d.displayName} />
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default DashboardSelector;
